/**
 * SunoStyleEditor — переиспользуемое UI-тело редактора стиля Suno.
 *
 * Используется в SunoStyleSheet (shared между desktop pill'ом в виджете
 * и MobileSettingsSheet строкой). Полностью controlled — не имеет
 * собственного state для value, всё flow через onStyleChange/onNegativeTagsChange.
 *
 * UX:
 *   - Стиль: TagsField — pill'ы известных жанров с × (крестик удаляет) +
 *     textarea для свободного текста + список chips-подсказок
 *   - «Улучшить через AI» (boost-style) + «Вернуть исходный»
 *   - Исключить: такой же TagsField с тем же списком MUSIC_STYLES
 *     (юзер видит что именно можно исключать, не нужно выдумывать)
 */
import { useCallback, useState } from 'react'
import { useCreditsGuard } from '../../../hooks/useCreditsGuard'
import { boostStyle, MUSIC_STYLES } from '../../../api/music'
import TokenPrice from '../../../components/TokenPrice'
import { opAvailable, providerOf } from '../utils/trackOperations'
import { Icon } from '../../../../public/icons/Icon.tsx'
import type { Model } from '../../../data/models'

interface Props {
	model: Model | null
	style: string
	negativeTags: string
	onStyleChange: (value: string) => void
	onNegativeTagsChange: (value: string) => void
	/** Лимит длины style-поля. Зависит от текущей параметрической операции:
	 *  add_vocals / add_instrumental → 500 (Suno API строже для этих ops);
	 *  generate / extend / cover / undefined → 1000. По умолчанию 1000 чтобы
	 *  не регрессировать caller'ы которые не передают (MobileSettingsSheet). */
	styleMaxLength?: number
}

// ── Helpers ──────────────────────────────────────────────────

/**
 * Парсит сырую строку "Pop, Dark vibes, Rock" на массив токенов
 * через запятую. Пробелы вокруг убираются, пустые отбрасываются.
 */
function splitTokens(raw: string): string[] {
	return raw
		.split(',')
		.map((s) => s.trim())
		.filter(Boolean)
}

/**
 * Разделяет токены на известные (matching suggestions list)
 * и свободный текст. Матчинг case-insensitive, дубли убираются.
 */
function partitionTokens(
	raw: string,
	suggestions: readonly string[],
): { tags: string[]; freeText: string } {
	const tokens = splitTokens(raw)
	const suggestionLower = suggestions.map((s) => s.toLowerCase())
	const tagSet = new Set<string>()
	const tags: string[] = []
	const rest: string[] = []

	for (const token of tokens) {
		const lower = token.toLowerCase()
		const idx = suggestionLower.indexOf(lower)
		if (idx >= 0) {
			// Нормализуем к оригинальному регистру из suggestions
			const canonical = suggestions[idx]
			if (!tagSet.has(canonical.toLowerCase())) {
				tagSet.add(canonical.toLowerCase())
				tags.push(canonical)
			}
		} else {
			rest.push(token)
		}
	}

	return { tags, freeText: rest.join(', ') }
}

/** Собирает tags + freeText обратно в сырую строку для API */
function joinTokens(tags: string[], freeText: string): string {
	const parts = [...tags]
	const trimmed = freeText.trim()
	if (trimmed) parts.push(trimmed)
	return parts.join(', ')
}

// ── TagsField — pill'ы с × + textarea + подсказки-chips ─────

interface TagsFieldProps {
	id: string
	value: string
	onChange: (value: string) => void
	suggestions: readonly string[]
	placeholder: string
	maxLength: number
	/** Метка над списком подсказок. Например «Популярные жанры» или «Примеры» */
	suggestionsLabel: string
}

function TagsField({
	id,
	value,
	onChange,
	suggestions,
	placeholder,
	maxLength,
	suggestionsLabel,
}: TagsFieldProps) {
	const { tags, freeText } = partitionTokens(value, suggestions)
	// Collapsed: показываем только первую строку chips, остальное — под кнопкой.
	// Экономит пространство + не пугает новичка списком из 29 жанров сразу.
	const [suggestionsExpanded, setSuggestionsExpanded] = useState(false)

	const addTag = useCallback(
		(tag: string) => {
			const { tags: currentTags, freeText: currentFree } = partitionTokens(value, suggestions)
			if (currentTags.some((t) => t.toLowerCase() === tag.toLowerCase())) return
			onChange(joinTokens([...currentTags, tag], currentFree))
		},
		[value, suggestions, onChange],
	)

	const removeTag = useCallback(
		(tag: string) => {
			const { tags: currentTags, freeText: currentFree } = partitionTokens(value, suggestions)
			onChange(
				joinTokens(
					currentTags.filter((t) => t.toLowerCase() !== tag.toLowerCase()),
					currentFree,
				),
			)
		},
		[value, suggestions, onChange],
	)

	const toggleTag = useCallback(
		(tag: string) => {
			if (tags.some((t) => t.toLowerCase() === tag.toLowerCase())) {
				removeTag(tag)
			} else {
				addTag(tag)
			}
		},
		[tags, addTag, removeTag],
	)

	const handleFreeTextChange = useCallback(
		(text: string) => {
			// Забираем текущее разделение (могло обновиться снаружи)
			const { tags: currentTags } = partitionTokens(value, suggestions)
			onChange(joinTokens(currentTags, text))
		},
		[value, suggestions, onChange],
	)

	return (
		<div className="suno-tags-field">
			{/* Pills сверху — выбранные известные жанры с крестиком */}
			{tags.length > 0 && (
				<div className="suno-tags-field__pills">
					{tags.map((tag) => (
						<span key={tag} className="suno-tags-field__pill">
							<span className="suno-tags-field__pill-label">{tag}</span>
							<button
								type="button"
								className="suno-tags-field__pill-remove"
								onClick={() => removeTag(tag)}
								aria-label={`Удалить «${tag}»`}
								title={`Удалить «${tag}»`}
							>
								×
							</button>
						</span>
					))}
				</div>
			)}

			{/* Textarea — свободный текст (всё что не в suggestions) */}
			<textarea
				id={id}
				className="suno-tags-field__textarea"
				value={freeText}
				onChange={(e) => handleFreeTextChange(e.target.value)}
				placeholder={placeholder}
				rows={2}
				maxLength={maxLength}
			/>

			{/* Chips-подсказки снизу */}
			<div className="suno-tags-field__suggestions-label">{suggestionsLabel}</div>
			<div
				className={`suno-tags-field__suggestions${
					suggestionsExpanded ? ' suno-tags-field__suggestions--expanded' : ''
				}`}
			>
				{suggestions.map((s) => {
					const active = tags.some((t) => t.toLowerCase() === s.toLowerCase())
					return (
						<button
							key={s}
							type="button"
							className={`suno-tags-field__chip${active ? ' suno-tags-field__chip--active' : ''}`}
							onClick={() => toggleTag(s)}
						>
							{s}
						</button>
					)
				})}
			</div>
			<button
				type="button"
				className="suno-tags-field__toggle-btn"
				onClick={() => setSuggestionsExpanded((v) => !v)}
			>
				{suggestionsExpanded
					? '▲ Свернуть'
					: `▼ Показать все (${suggestions.length})`}
			</button>
		</div>
	)
}

// ── Main component ────────────────────────────────────────────

export default function SunoStyleEditor({
	model,
	style,
	negativeTags,
	onStyleChange,
	onNegativeTagsChange,
	styleMaxLength = 1000,
}: Props) {
	const guard = useCreditsGuard()
	const [previousStyle, setPreviousStyle] = useState<string | null>(null)
	const [boostLoading, setBoostLoading] = useState(false)
	const [boostError, setBoostError] = useState<string | null>(null)

	const boostPrice =
		model?.variants?.find((v) => v.key === 'boost-style' || v.key === 'boost_style')
			?.credits_price ?? null

	const provider = providerOf(model?.id)

	const handleBoost = useCallback(async () => {
		if (!style.trim() || boostLoading) return
		setBoostLoading(true)
		setBoostError(null)
		try {
			const sunoModel = model?.id?.replace('suno-', '').toUpperCase().replace(/-/g, '_') || 'V5_5'
			const res = await boostStyle({ content: style.trim(), model: sunoModel })
			if (res.ok && res.result) {
				setPreviousStyle(style)
				onStyleChange(res.result)
			} else {
				setBoostError(res.error || 'Не удалось улучшить стиль')
			}
		} catch (err) {
			setBoostError(err instanceof Error ? err.message : 'Сетевая ошибка')
		} finally {
			setBoostLoading(false)
		}
	}, [style, boostLoading, onStyleChange, model?.id])

	const handleRestorePrev = useCallback(() => {
		if (previousStyle == null) return
		onStyleChange(previousStyle)
		setPreviousStyle(null)
	}, [previousStyle, onStyleChange])

	// Любое ручное изменение стиля сбрасывает возможность undo
	const handleStyleChangeWithReset = useCallback(
		(value: string) => {
			onStyleChange(value)
			setPreviousStyle(null)
		},
		[onStyleChange],
	)

	return (
		<>
			{/* ── Стиль ── */}
			{/* Обёртка .suno-style-sheet__style-block — использует тур
			    (SpotlightTour) как якорь для шага «Задайте стиль песни». */}
			<div className="suno-style-sheet__style-block">
				<label className="suno-style-sheet__label" htmlFor="suno-style-input">
					Стиль
				</label>
				<TagsField
					id="suno-style-input"
					value={style}
					onChange={handleStyleChangeWithReset}
					suggestions={MUSIC_STYLES}
					placeholder="Опишите желаемое звучание, например: dark mysterious vibes"
					maxLength={styleMaxLength}
					suggestionsLabel="Популярные жанры (клик — добавить)"
				/>
			</div>

			{/* Boost row */}
			{opAvailable('boost_style', provider) && (
			<div className="suno-style-sheet__boost-row">
				<button
					type="button"
					className={`suno-style-sheet__boost-btn${boostLoading ? ' suno-style-sheet__boost-btn--loading' : ''}`}
					onClick={handleBoost}
					disabled={!style.trim() || boostLoading || guard.shortFor(boostPrice)}
					title={boostPrice !== null && guard.shortFor(boostPrice) ? guard.titleFor(boostPrice) : undefined}
				>
					<Icon id="gen-prompt" size={13} />
					<span>{boostLoading ? 'Улучшаем…' : 'Улучшить через AI'}</span>
					{boostPrice !== null && !boostLoading && (
						<span className="suno-style-sheet__boost-price">
							<TokenPrice value={boostPrice} iconSize={10} />
						</span>
					)}
				</button>

				{previousStyle !== null && !boostLoading && (
					<button
						type="button"
						className="suno-style-sheet__restore-btn"
						onClick={handleRestorePrev}
					>
						Вернуть исходный
					</button>
				)}
			</div>
			)}

			{boostError && <div className="suno-style-sheet__boost-error">{boostError}</div>}

			<div className="suno-style-sheet__divider" />

			{/* ── Negative tags ── */}
			{/* Обёртка для тура — шаг «Исключите то что не хотите». */}
			<div className="suno-style-sheet__negative-block">
				<label className="suno-style-sheet__label" htmlFor="suno-neg-input">
					Исключить <span className="suno-style-sheet__opt">(необязательно)</span>
				</label>
				<TagsField
					id="suno-neg-input"
					value={negativeTags}
					onChange={onNegativeTagsChange}
					suggestions={MUSIC_STYLES}
					placeholder="Или напишите что-то своё, например: Screaming, Loud drums"
					maxLength={500}
					suggestionsLabel="Примеры (клик — исключить)"
				/>
				<div className="suno-style-sheet__hint">
					Стили которых НЕ должно быть в песне.
				</div>
			</div>
		</>
	)
}
